import React from 'react';
import { Sun, Moon, Monitor } from 'lucide-react';
import {
  useTenantAppearance,
  type TenantTheme,
} from '../../contexts/TenantAppearanceContext';

function classNames(...classes: Array<string | false | undefined>): string {
  return classes.filter(Boolean).join(' ');
}

interface TenantAppearanceToggleProps {
  /** `sidebar` matches the main dashboard footer; `compact` fits drawers and login. */
  variant?: 'sidebar' | 'compact';
  /** Mobile menu uses light/dark only; desktop sidebar can include system. */
  themeOptions?: 'light-dark' | 'full';
}

export const TenantAppearanceToggle: React.FC<TenantAppearanceToggleProps> = ({
  variant = 'sidebar',
  themeOptions = 'full',
}) => {
  const { theme, setTheme } = useTenantAppearance();

  const allOptions: Array<{
    value: TenantTheme;
    Icon: React.ComponentType<{ className?: string }>;
    label: string;
  }> = [
    { value: 'light', Icon: Sun, label: 'Light mode' },
    { value: 'system', Icon: Monitor, label: 'System default' },
    { value: 'dark', Icon: Moon, label: 'Dark mode' },
  ];
  const options =
    themeOptions === 'light-dark'
      ? allOptions.filter((option) => option.value === 'light' || option.value === 'dark')
      : allOptions;

  if (variant === 'compact') {
    return (
      <div className="flex w-full items-center justify-between px-2 py-2">
        <span className="text-xs font-medium text-gray-400 dark:text-gray-500">
          Appearance
        </span>
        <div className="flex items-center gap-0.5 rounded-md bg-gray-100 p-0.5 dark:bg-gray-800">
          {options.map(({ value, Icon, label }) => (
            <button
              key={value}
              type="button"
              onClick={() => setTheme(value)}
              title={label}
              className={classNames(
                theme === value
                  ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-white'
                  : 'text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300',
                'rounded p-1 transition-all',
              )}
            >
              <Icon className="size-3.5" aria-hidden="true" />
              <span className="sr-only">{label}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between border-t border-gray-100 px-6 py-2 dark:border-white/5">
      <span className="text-xs font-medium text-gray-400 dark:text-gray-500">
        Appearance
      </span>
      <div className="flex items-center gap-0.5 rounded-md bg-gray-100 p-0.5 dark:bg-gray-800">
        {options.map(({ value, Icon, label }) => (
          <button
            key={value}
            type="button"
            onClick={() => setTheme(value)}
            title={label}
            className={classNames(
              theme === value
                ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-white'
                : 'text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300',
              'rounded p-1 transition-all',
            )}
          >
            <Icon className="size-3.5" aria-hidden="true" />
            <span className="sr-only">{label}</span>
          </button>
        ))}
      </div>
    </div>
  );
};
